"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { NavArrowLeft, NavArrowRight } from "iconoir-react";

type StackPhoto = {
  src: string;
  alt: string;
  width: number;
  height: number;
};

// Mixed aspect ratios on purpose — the stack sizes each one to fit.
const PHOTOS: StackPhoto[] = [
  {
    src: "/photos/events_umojafest_1.jpg",
    alt: "Umoja Fest street celebration",
    width: 2048,
    height: 1365,
  },
  {
    src: "/photos/events_umojafest_2.jpg",
    alt: "Umoja Fest performer in regalia",
    width: 1638,
    height: 2048,
  },
  {
    src: "/photos/smiling_children.jpg",
    alt: "Children smiling at a community gathering",
    width: 2048,
    height: 1365,
  },
  {
    src: "/photos/street_crossing.jpg",
    alt: "Crossing a Central District street",
    width: 2048,
    height: 2048,
  },
  {
    src: "/photos/smiling_people_portrait.jpg",
    alt: "Neighbors smiling together",
    width: 1638,
    height: 2048,
  },
  {
    src: "/photos/food_communion_1.jpg",
    alt: "A plate served at Communion",
    width: 2500,
    height: 1666,
  },
  {
    src: "/photos/black_panther_car.jpg",
    alt: "Black Panther Party car on display",
    width: 2048,
    height: 1365,
  },
  {
    src: "/photos/africatown_plaza.jpg",
    alt: "Africatown Plaza",
    width: 2048,
    height: 1365,
  },
];

// How the top three cards sit relative to one another.
const DEPTH_STYLES = [
  { x: 0, y: 0, rotate: -1.5, scale: 1 },
  { x: 14, y: 10, rotate: 3.5, scale: 0.96 },
  { x: -10, y: 18, rotate: -5, scale: 0.92 },
];

const VISIBLE_DEPTH = DEPTH_STYLES.length;
const WHEEL_COOLDOWN_MS = 320;
// Horizontal distance a touch/pen drag must cover before it counts as a swipe.
const SWIPE_THRESHOLD_PX = 40;

const shuffle = <T,>(items: T[]): T[] => {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
};

interface PhotoStackProps {
  photos?: StackPhoto[];
  className?: string;
}

export const PhotoStack = ({
  photos = PHOTOS,
  className = "",
}: PhotoStackProps) => {
  // Shuffled after mount so the server and the first client render agree.
  const [order, setOrder] = useState<StackPhoto[]>(photos);
  const [activeIndex, setActiveIndex] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);
  const lastWheelRef = useRef(0);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setOrder(shuffle(photos));
  }, [photos]);

  const total = order.length;

  const cycle = useCallback(
    (direction: number) => {
      if (total === 0) return;
      setActiveIndex((prev) => (prev + direction + total) % total);
    },
    [total],
  );

  // Non-passive so the scroll can be claimed by the stack instead of the panel.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const handleWheel = (event: WheelEvent) => {
      const delta =
        Math.abs(event.deltaY) > Math.abs(event.deltaX)
          ? event.deltaY
          : event.deltaX;
      if (delta === 0) return;

      event.preventDefault();

      const now = performance.now();
      if (now - lastWheelRef.current < WHEEL_COOLDOWN_MS) return;
      lastWheelRef.current = now;

      cycle(delta > 0 ? 1 : -1);
    };

    stage.addEventListener("wheel", handleWheel, { passive: false });
    return () => stage.removeEventListener("wheel", handleWheel);
  }, [cycle]);

  const handlePointerDown = (event: React.PointerEvent) => {
    if (event.pointerType === "mouse") return;
    swipeStartRef.current = { x: event.clientX, y: event.clientY };
  };

  const handlePointerEnd = (event: React.PointerEvent) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    // Ignore drags that are really an attempt to scroll the panel.
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy)) {
      return;
    }

    cycle(dx < 0 ? 1 : -1);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      cycle(1);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      cycle(-1);
    }
  };

  if (total === 0) return null;

  return (
    <div className={`photo-stack group relative w-full ${className}`}>
      {/* touch-action pan-y: vertical scrolling stays with the panel,
          horizontal drags belong to the stack. */}
      <div
        ref={stageRef}
        tabIndex={0}
        role="group"
        aria-roledescription="photo stack"
        aria-label={`Photo ${activeIndex + 1} of ${total}`}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerEnd}
        onPointerCancel={() => {
          swipeStartRef.current = null;
        }}
        style={{ touchAction: "pan-y" }}
        className="relative h-full w-full outline-none"
      >
        {order.map((photo, index) => {
          const depth = (index - activeIndex + total) % total;
          const isVisible = depth < VISIBLE_DEPTH;
          const style = DEPTH_STYLES[Math.min(depth, VISIBLE_DEPTH - 1)];

          return (
            <div
              key={photo.src}
              aria-hidden={depth !== 0}
              className="absolute inset-0 flex items-center justify-center transition-all duration-500 ease-out motion-reduce:transition-none"
              style={{
                transform: `translate(${style.x}px, ${style.y}px) rotate(${style.rotate}deg) scale(${isVisible ? style.scale : 0.9})`,
                opacity: isVisible ? 1 : 0,
                zIndex: total - depth,
                pointerEvents: depth === 0 ? "auto" : "none",
              }}
            >
              <Image
                src={photo.src}
                alt={depth === 0 ? photo.alt : ""}
                width={photo.width}
                height={photo.height}
                sizes="(max-width: 767px) 80vw, 30vw"
                className="h-auto w-auto max-h-full max-w-full rounded-2xl object-contain shadow-2xl shadow-black/40"
              />
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => cycle(-1)}
        aria-label="Previous photo"
        className="stack-arrow hidden md:block absolute -left-6 lg:-left-10 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 rounded-full bg-background/70 p-2 text-text-on-background opacity-0 backdrop-blur-sm transition-opacity duration-300 hover:bg-background focus-visible:opacity-100 group-hover:opacity-70 group-hover:hover:opacity-100"
      >
        <NavArrowLeft className="h-5 w-5" />
      </button>

      <button
        type="button"
        onClick={() => cycle(1)}
        aria-label="Next photo"
        className="stack-arrow hidden md:block absolute -right-6 lg:-right-10 top-1/2 z-20 translate-x-1/2 -translate-y-1/2 rounded-full bg-background/70 p-2 text-text-on-background opacity-0 backdrop-blur-sm transition-opacity duration-300 hover:bg-background focus-visible:opacity-100 group-hover:opacity-70 group-hover:hover:opacity-100"
      >
        <NavArrowRight className="h-5 w-5" />
      </button>
    </div>
  );
};
